/**
 * The mobile form of the nav-rail primitive — a `<schmancy-navigation-rail>`
 * with no ancestor rail (`depth === 0`) renders one of these automatically
 * (navigation-rail.ts's own render()), so a consumer never mounts it by
 * hand. Ported from momo-starter's `app-navigation-rail.view.ts`, which
 * proved the mechanics live: pointer-capture drag, `pointerup`/
 * `pointercancel` termination, the tap-vs-drag threshold, and edge-snap
 * with a persisted parked position.
 */
import { SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { show } from '@schmancy/overlay'
import { inExemptLayer } from '@schmancy/overlay/overlay.stack'
import { state } from '@schmancy/state'
import { css, html } from 'lit'
import { property } from 'lit/decorators.js'
import { fromEvent, merge, Subject, takeUntil } from 'rxjs'
import { exhaustMap, filter, startWith, switchMap, take, tap } from 'rxjs/operators'
import type { RegisteredRail } from './context'
import './navigation-rail-sheet'

/** Fixed-position offsets (px) of the puck from the viewport's top-left. */
type PuckPosition = { left: number; top: number }

/** Per-tab — placement is an ephemeral convenience, not a durable preference. */
const puckPosition = state<PuckPosition | null>('schmancy/nav-rail-puck').session(null)

const PUCK_SIZE = 56 // px
const PUCK_INSET = 8 // px — minimum gap from viewport edge
/** Below this movement (px), a pointer interaction is a tap, not a drag. */
const TAP_THRESHOLD_PX = 6

@schmancyElement('schmancy-navigation-rail-puck')
export class SchmancyNavigationRailPuck extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: none;
			}

			/* position:fixed drops the puck out of whatever layout the rail sits
			 * in. Transition writes 'left'/'top' in JS and lets CSS glide it,
			 * suppressed during drag via data-dragging so the puck tracks the
			 * finger instantly.
			 *
			 * The resting corner is declared HERE rather than computed in JS
			 * because only CSS can read env() — the strip a home indicator or a
			 * rounded display corner occupies, which a bottom-anchored control
			 * has to clear and which no measurement in script reports. */
			@media (max-width: 767px) {
				:host {
					display: flex;
					align-items: center;
					justify-content: center;
					position: fixed;
					/* Where a puck nobody has moved parks. A fixed control over a
					 * full-bleed column overlaps something wherever it sits, so the
					 * only question is where a reader is not: a fraction of the
					 * viewport height at the leading edge is the middle of the
					 * reading column, over the start of whatever line is there,
					 * while a corner is where a floating control is expected and
					 * where no line begins. Logical insets, so an RTL document parks
					 * it at the bottom left — the same corner read its own way. */
					inset-block-end: calc(env(safe-area-inset-bottom, 0px) + ${PUCK_INSET}px);
					inset-inline-end: calc(env(safe-area-inset-right, 0px) + ${PUCK_INSET}px);
					width: ${PUCK_SIZE}px;
					height: ${PUCK_SIZE}px;
					border-radius: var(--schmancy-radius-circle, 50%);
					background: color-mix(in srgb, var(--schmancy-sys-color-surface-containerHigh) 78%, transparent);
					backdrop-filter: blur(var(--schmancy-sys-glass-blur));
					box-shadow:
						0 4px 20px color-mix(in oklch, var(--schmancy-sys-color-shadow) 35%, transparent),
						inset 0 1px 0 color-mix(in srgb, var(--schmancy-sys-color-surface-on) 8%, transparent);
					opacity: 0.82;
					touch-action: none;
					cursor: pointer;
					z-index: 400;
					transition:
						left 0.22s ease-out,
						top 0.22s ease-out,
						opacity 0.15s ease,
						transform 0.1s ease;
					user-select: none;
					-webkit-user-select: none;
					-webkit-touch-callout: none;
					-webkit-user-drag: none;
					-webkit-tap-highlight-color: transparent;
				}

				/* A puck with a placement of its own — dragged there now, or
				 * restored from where its user parked it — is positioned by
				 * left/top in JS, and the resting corner has to YIELD rather than
				 * merely lose: left and right both set is over-constrained, and
				 * which of the two the browser drops depends on the document's
				 * direction, so in RTL the corner would win and strand the drag. */
				:host([data-placed]) {
					inset-block-end: auto;
					inset-inline-end: auto;
				}

				:host([data-dragging]) {
					opacity: 1;
					transition: opacity 0.15s ease;
				}

				:host(:hover),
				:host(:focus-within) {
					opacity: 1;
				}

				:host(:active) {
					opacity: 1;
					transform: scale(0.97);
				}
			}
		`,
	]

	/** The root rail — the level the puck's cascade starts at. Below `md` the
	 *  rail's own items are off screen too, so unlike the inline mount this
	 *  one draws from depth 0 rather than from the first descendant. */
	@property({ attribute: false })
	node!: RegisteredRail

	private readonly hamburgerTap$ = new Subject<void>()

	/**
	 * Put the puck back where its placement says it belongs: at the position
	 * its user parked it at, clamped to the viewport actually painting it now,
	 * or — parked nowhere — back under the resting corner in this component's
	 * own styles, by taking the inline left/top that outranks it away.
	 *
	 * Not a computed corner: only CSS reads env(), so the strip a home
	 * indicator occupies is part of the default only while CSS owns it.
	 *
	 * Two callers, both of them a moment the puck stops being driven by a
	 * gesture: entering mobile, and a press that turned out to be a tap.
	 */
	private applyParkedPosition(): void {
		const saved = puckPosition.value
		if (!saved) {
			this.removeAttribute('data-placed')
			this.style.removeProperty('left')
			this.style.removeProperty('top')
			return
		}
		const maxLeft = window.innerWidth - PUCK_SIZE - PUCK_INSET
		const maxTop = window.innerHeight - PUCK_SIZE - PUCK_INSET
		this.style.left = `${Math.min(Math.max(saved.left, PUCK_INSET), maxLeft)}px`
		this.style.top = `${Math.min(Math.max(saved.top, PUCK_INSET), maxTop)}px`
		this.setAttribute('data-placed', '')
	}

	connectedCallback() {
		super.connectedCallback()
		const mobile = window.matchMedia('(max-width: 767px)')

		merge(
			fromEvent<MediaQueryListEvent>(mobile, 'change').pipe(
				startWith(mobile),
				tap(({ matches }) => {
					this.role = matches ? 'button' : null
					this.ariaLabel = matches ? 'Open navigation' : null
					if (matches) {
						this.tabIndex = 0
					} else {
						this.removeAttribute('tabindex')
						this.removeAttribute('data-placed')
						this.style.removeProperty('left')
						this.style.removeProperty('top')
					}
				}),
				filter(({ matches }) => matches),
				// A desktop mount can enter mobile later, against a viewport this
				// puck has never been measured against.
				tap(() => this.applyParkedPosition()),
			),

			// The overlay's result is the value the cascade closed on, and the
			// puck does nothing with it: the renderer has already dispatched
			// `navigate` on the rail that OWNS that item. Re-dispatching it here
			// instead is what made every level past the root unroutable — the
			// puck is one of the ROOT rail's own render() children, so the event
			// surfaced inside the root rail's shadow root and reached only the
			// root consumer, which a depth-2 value names nothing in.
			this.hamburgerTap$.pipe(
				exhaustMap(() =>
					show(
						// `footer` and `header`: below `md` this sheet is the only navigation
						// on screen, so whatever the rail closes its column with — a sign-out,
						// an account control — has to come with it, and the row the consumer
						// authored for its head (`sheet-header`: a mark, a plate) leads it.
						() =>
							html`<schmancy-navigation-rail-sheet
								?data-overlay-exempt=${inExemptLayer(this)}
								header
								footer
								class="w-72 max-w-full p-3"
								.node=${this.node}
							></schmancy-navigation-rail-sheet>`,
						{
							ariaLabel: 'Navigation',
							// GROW sizes the surface to the cascade's own natural content
							// (autoPlacement into the roomiest region + both-axis shift onto
							// screen) instead of the NON-GROW default's one-sided clamp —
							// the geometry an indented column needs, parked at either edge.
							// `preferredPlacement` only biases the tie-break between
							// equally-roomy regions; autoPlacement's own room-based choice
							// (and the shift that follows it) is what actually keeps the
							// column on screen regardless of which edge the puck is parked at.
							grow: true,
							// The puck's live box, not its inline style: until the user
							// parks it somewhere the resting corner is CSS's and the
							// inline style is empty, where parseFloat('') is NaN and
							// every comparison with it is false — the bias would then be
							// 'bottom-end' whichever edge the puck actually sat at.
							preferredPlacement:
								this.getBoundingClientRect().left < window.innerWidth / 2 ? 'bottom-start' : 'bottom-end',
						},
					).pipe(
						take(1),
						// Widened past `md`, the column is the navigation on screen again, and
						// the footer this sheet holds — on some shells the only sign-out —
						// belongs back in it: ending the subscription closes the overlay, and
						// the sheet's teardown returns the footer.
						takeUntil(fromEvent<MediaQueryListEvent>(mobile, 'change').pipe(filter(({ matches }) => !matches))),
					),
				),
			),

			fromEvent<KeyboardEvent>(this, 'keydown').pipe(
				filter(() => mobile.matches),
				filter(e => e.key === 'Enter' || e.key === ' '),
				tap(e => {
					e.preventDefault()
					this.hamburgerTap$.next()
				}),
			),

			// switchMap (latest-wins): a second pointerdown before the previous
			// pointerup (multi-touch) tracks the newest pointer, not locked to
			// the first — exhaustMap would strand the puck if the first pointer
			// released off-screen. Pointer capture keeps pointermove/pointerup
			// arriving even after the finger slides off the element.
			fromEvent<PointerEvent>(this, 'pointerdown').pipe(
				filter(() => mobile.matches),
				switchMap(down => {
					const startX = down.clientX
					const startY = down.clientY
					this.setPointerCapture(down.pointerId)
					// Suppress the snap transition while dragging so the puck tracks
					// the finger without lag.
					this.setAttribute('data-dragging', '')
					// Take placement over from the resting corner at the coordinates
					// the puck already paints at, so the handover is invisible and the
					// release below always has a position to read: a gesture the OS
					// takes back before it moves used to read an empty inline style as
					// 0 and park the puck in the TOP-left corner — persisted.
					// Measured from the box's CENTRE rather than its left/top edge
					// because :host(:active) scales the puck about that centre, and a
					// scaled edge would shift it by half the shrink on every press.
					const resting = this.getBoundingClientRect()
					this.style.left = `${Math.round(resting.left + resting.width / 2 - PUCK_SIZE / 2)}px`
					this.style.top = `${Math.round(resting.top + resting.height / 2 - PUCK_SIZE / 2)}px`
					this.setAttribute('data-placed', '')

					return fromEvent<PointerEvent>(this, 'pointermove').pipe(
						tap(move => {
							const rawLeft = move.clientX - PUCK_SIZE / 2
							const rawTop = move.clientY - PUCK_SIZE / 2
							const clampedLeft = Math.max(PUCK_INSET, Math.min(rawLeft, window.innerWidth - PUCK_SIZE - PUCK_INSET))
							const clampedTop = Math.max(PUCK_INSET, Math.min(rawTop, window.innerHeight - PUCK_SIZE - PUCK_INSET))
							// Direct style write — not a state change, no re-render needed.
							this.style.left = `${clampedLeft}px`
							this.style.top = `${clampedTop}px`
						}),
						takeUntil(
							// End on pointerup OR pointercancel. Touch devices fire
							// pointercancel (not pointerup) whenever the system steals the
							// gesture — incoming call, notification pull-down, a second
							// finger, an OS edge-swipe. Without this leg the session never
							// terminates: the puck stays stuck in data-dragging and the
							// captured pointer leaks.
							merge(fromEvent<PointerEvent>(this, 'pointerup'), fromEvent<PointerEvent>(this, 'pointercancel')).pipe(
								take(1),
								tap(end => {
									this.removeAttribute('data-dragging')
									if (this.hasPointerCapture(end.pointerId)) {
										this.releasePointerCapture(end.pointerId)
									}

									const dx = end.clientX - startX
									const dy = end.clientY - startY
									// A clean lift that barely moved is a tap → open the sheet. A
									// canceled gesture is never a tap, even if it didn't move.
									if (end.type === 'pointerup' && Math.sqrt(dx * dx + dy * dy) < TAP_THRESHOLD_PX) {
										// A tap is not a placement: hand the puck back to whatever
										// governed before the press, so one that was resting in the
										// corner keeps FOLLOWING that corner through a rotation or a
										// safe-area change rather than freezing at the pixels the
										// pointerdown above froze it at.
										this.applyParkedPosition()
										this.hamburgerTap$.next()
										return
									}

									// Drag (or canceled) release: snap to nearest horizontal
									// edge. The CSS transition is back (data-dragging removed
									// above), so this style write produces the glide. Read the
									// style the pointerdown above set and the drag then kept
									// current — offsetLeft/Top are unreliable for a
									// position:fixed element (null offsetParent).
									const currentLeft = parseFloat(this.style.left)
									const currentTop = parseFloat(this.style.top)
									const midpoint = (window.innerWidth - PUCK_SIZE) / 2
									const snappedLeft = currentLeft < midpoint ? PUCK_INSET : window.innerWidth - PUCK_SIZE - PUCK_INSET
									this.style.left = `${snappedLeft}px`
									// Remember where the user parked it (per-tab) so a refresh restores it.
									puckPosition.set({ left: snappedLeft, top: currentTop })
								}),
							),
						),
					)
				}),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe()
	}

	protected render() {
		return html`<schmancy-icon>menu</schmancy-icon>`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-navigation-rail-puck': SchmancyNavigationRailPuck
	}
}
